import { Badge, Card, Descriptions, Space, Tag, Typography } from "antd";
import {
  Briefcase,
  ChevronRight,
  Clock,
  LogIn,
  LogOut,
  MapPin,
  Package,
  Truck,
  FileText,
  MessageSquare,
  User,
  Users
} from "lucide-react";
import React, { FC } from "react";
import { TServiceTicket } from "~/types";
import {
  calculateCubicFeetInventory,
  capitalize,
  formatDimensions,
  formatFullAddress,
  formatToAMPM,
  getClientName
} from "~/utils";
import { formatStaffName } from "~/utils/format-staff-name";
import { useNavigate } from 'react-router-dom';
import { ServiceType, InventoryType } from "~/utils/constants";
import { ServiceCompletionDetails } from "~/components/common/service-completion-details";


import dayjs from "dayjs";

const formatDateTimeWithDay = (serviceDatetime: string | Date, startDatetime?: string | Date, duration?: number) => {
  if (!serviceDatetime) return '';

  // Use serviceDatetime for the date and startDatetime for the time
  const serviceDate = new Date(serviceDatetime);
  const startTime = startDatetime ? new Date(startDatetime) : null;

  // Get day of the week from serviceDatetime
  const dayOfWeek = serviceDate.toLocaleDateString('en-US', { weekday: 'long' });

  // Get month name (up to 4 letters), day, and year
  const monthName = serviceDate.toLocaleDateString('en-US', { month: 'short' }); // Jan, Feb, etc.
  const day = serviceDate.getDate();
  const year = serviceDate.getFullYear();
  const formattedDate = `${monthName} ${day}, ${year}`; // e.g., "Jan 15, 2025"

  // Get time from startDatetime, fallback to serviceDatetime if not available
  const timeSource = startTime || serviceDate;
  const time = timeSource.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  return (
    <div className="text-center leading-tight">
      <div className="font-medium">{time}</div>
      <div className="font-medium">{dayOfWeek} {formattedDate}</div>
      {duration && (
        <div className="font-medium">{duration} Hrs</div>
      )}
    </div>
  );
};

const { Text } = Typography;



interface ContainerInfoCardProps {
  serviceTicket: TServiceTicket;
  variant?: "compact" | "detailed" | "summary" | "simpleschedule";
  showHeader?: boolean;
  className?: string;
  bookingId?: string;
  onInventoryClick?: (inventory: any) => void;
}

export const ContainerInfoCard: FC<ContainerInfoCardProps> = ({
  serviceTicket,
  variant = "detailed",
  showHeader = true,
  className = "",
  bookingId,
  onInventoryClick,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const getServiceTypeColor = (serviceType: ServiceType) => {
    switch (serviceType) {
      case ServiceType.COLLECTION:
        return "blue";
      case ServiceType.DELIVERY:
        return "blue";
      case ServiceType.FABRICATION_SERVICE:
        return "purple";
      case ServiceType.WAREHOUSE_SERVICE:
        return "purple";
      case ServiceType.SITE_SERVICE:
        return "green";
      default:
        return "blue";
    }
  };

  // Get background color based on service type and vessel color
  const getServiceBackgroundStyle = (serviceType: ServiceType, vesselColor?: string) => {
    // For COLLECTION and DELIVERY, use vessel color
    if (serviceType === ServiceType.COLLECTION || serviceType === ServiceType.DELIVERY) {
      if (!vesselColor) {
        return { backgroundColor: '#dbeafe' }; // default light blue
      }

      const colorMap: Record<string, string> = {
        'tomato': '#fee2e2',      // red-100
        'flamingo': '#fce7f3',    // pink-100
        'tangerine': '#ffedd5',   // orange-100
        'banana': '#fef9c3',      // yellow-100
        'sage': '#dcfce7',        // green-100
        'basil': '#d1fae5',       // green-200
        'peacock': '#dbeafe',     // blue-100
        'blueberry': '#dbeafe',   // blue-100
        'lavender': '#e9d5ff',    // purple-200
        'grape': '#e9d5ff',       // purple-200
        'graphite': '#e5e7eb',    // gray-200
      };

      return { backgroundColor: colorMap[vesselColor] || '#dbeafe' };
    }

    // SITE_SERVICE - contrasting grey
    if (serviceType === ServiceType.SITE_SERVICE) {
      return { backgroundColor: '#d1d5db' }; // gray-300
    }

    // FABRICATION_SERVICE and WAREHOUSE_SERVICE - light purple
    if (serviceType === ServiceType.FABRICATION_SERVICE || serviceType === ServiceType.WAREHOUSE_SERVICE) {
      return { backgroundColor: '#f3e8ff' }; // purple-100
    }

    // Default
    return { backgroundColor: '#f3f4f6' }; // gray-100
  };

  const getServiceIcon = (serviceType: ServiceType) => {
    switch (serviceType) {
      case ServiceType.COLLECTION:
      case ServiceType.DELIVERY:
        return <Truck size={16} />;
      case ServiceType.WAREHOUSE_SERVICE:
        return <Package size={16} />;
      default:
        return <Package size={16} />;
    }
  };

  // Helper function to get service ticket status colors
  const getServiceTicketStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "pending":
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "scheduled":
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "in-progress":
      case "started":
        return "bg-purple-100 text-purple-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      case "canceled":
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Helper function to format service ticket status text
  const formatServiceTicketStatus = (status: string) => {
    return status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase().replace("-", " ");
  };

const navigate = useNavigate();

  const inventoryStats = serviceTicket.inventories
    ? calculateCubicFeetInventory(serviceTicket.inventories)
    : { totalCubicFeet: 0, totalWeight: 0 };

  const handleViewInInventory = (inventory: any) => {
    // Use inventory ref or title for search to ensure the item appears in results
    const searchTerm = inventory.inventoryRef || inventory.title || "";
    const params = new URLSearchParams();
    params.set("item", inventory.id);
    if (searchTerm) {
      params.set("search", searchTerm);
    }
    navigate(`/inventory?${params.toString()}`);
  };

const renderCompactView = () => {
  const vesselColor = serviceTicket.vessel?.color;
  const backgroundStyle = getServiceBackgroundStyle(serviceTicket.serviceType, vesselColor);

  return (
  <div
    className={`border rounded-lg p-3 relative overflow-hidden ${className}`}
    style={{ ...backgroundStyle, position: 'relative', zIndex: 1 }}
  >
    <div className="flex justify-between items-start mb-2">
      <div className="flex items-center space-x-2">
        {getServiceIcon(serviceTicket.serviceType)}
        <span className="text-sm font-bold text-gray-900">
          {capitalize(serviceTicket.serviceType)}
        </span>
        <Text strong>{serviceTicket.serial}</Text>
      </div>
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getServiceTicketStatusColor(serviceTicket.status)}`}>
        {formatServiceTicketStatus(serviceTicket.status)}
      </span>
    </div>

    {/* Service Client (Primary) */}
    <div className="mb-2">
      <div className="font-medium text-gray-900 text-sm truncate">
        {serviceTicket.tempLocationData?.name || serviceTicket.client?.companyName || 'No Client'}
      </div>
      
      {/* Service Location Address */}
      {(() => {
        // Safe address extraction for service location
        let addressText = '';
        
        if (serviceTicket.address && typeof serviceTicket.address === 'object') {
          // Extract string from address object
          if (typeof serviceTicket.address.addressName === 'string') {
            addressText = serviceTicket.address.addressName;
          } else if (typeof serviceTicket.address.address === 'string') {
            addressText = serviceTicket.address.address;
          } else {
            // Build from city/state
            const city = typeof serviceTicket.address.city === 'string' ? serviceTicket.address.city : '';
            const state = typeof serviceTicket.address.state === 'string' ? serviceTicket.address.state : '';
            addressText = `${city} ${state}`.trim();
          }
        } else if (typeof serviceTicket.address === 'string') {
          addressText = serviceTicket.address;
        } else if (typeof serviceTicket.location === 'string') {
          addressText = serviceTicket.location;
        }
        
        return addressText ? (
          <div className="text-xs text-gray-500 truncate mt-1">
            {addressText}
          </div>
        ) : null;
      })()}
    </div>

    <div className="space-y-1 text-sm">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Text type="secondary" className="text-xs">Time:</Text>
          <div className="text-xs font-medium">{formatToAMPM(serviceTicket.startDatetime)}</div>
        </div>
        <div>
          <Text type="secondary" className="text-xs">Items:</Text>
          <div className="text-xs font-medium">
            {serviceTicket.inventories?.length > 0
              ? `${serviceTicket.inventories.length} pkgs • ${inventoryStats.totalCubicFeet} ft³`
              : serviceTicket.estimateTemplates?.length > 0
                ? `Est: ${serviceTicket.estimateTemplates.reduce((sum: number, t: any) => sum + (t.quantity || 0), 0)} pkgs`
                : serviceTicket.estimatedItemCount
                  ? `Est: ~${serviceTicket.estimatedItemCount} items`
                  : '0 pkgs'}
          </div>
        </div>
      </div>

      <div className="text-xs text-gray-600">
        {serviceTicket.vessel?.name || 'No Vehicle'}
      </div>

      {/* Staff and Add-Ons side by side */}
      {(serviceTicket.staffs?.length > 0 || serviceTicket.addOnServices?.length > 0) && (
        <div className="grid grid-cols-2 gap-2 text-xs">
          {/* Staff Names */}
          {serviceTicket.staffs && serviceTicket.staffs.length > 0 && (
            <div>
              <Text type="secondary" className="text-xs">Staff:</Text>
              <div className="text-gray-700 truncate">
                {serviceTicket.staffs.map((staff, index) => {
                  return (
                    <span key={staff.id}>
                      {formatStaffName(staff)}
                      {index < serviceTicket.staffs.length - 1 && ", "}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add-On Services */}
          {serviceTicket.addOnServices && serviceTicket.addOnServices.length > 0 && (
            <div>
              <Text type="secondary" className="text-xs">Add-Ons:</Text>
              <div className="text-gray-700 truncate">
                {serviceTicket.addOnServices.map((addOn, index) => (
                  <span key={addOn.id}>
                    {addOn.name}
                    {index < serviceTicket.addOnServices.length - 1 && ", "}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  </div>
  );
};

  const renderSummaryView = () => {
    // Add click handler function for navigation
    const handleCardClick = () => {
      // Use the passed bookingId prop first, then fall back to serviceTicket's jobTicket id
      const targetBookingId = bookingId || serviceTicket.jobTicket?.id;

      if (!targetBookingId) {
        console.warn('No booking ID available for navigation', {
          passedBookingId: bookingId,
          serviceTicket: serviceTicket,
          jobTicket: serviceTicket.jobTicket
        });
        return;
      }
      navigate(`/bookings/${targetBookingId}?tab=services&serviceId=${serviceTicket.id}`);
    };

    return (
    <Card
      size="small"
      className={`relative ${className} cursor-pointer hover:shadow-lg transition-shadow duration-200 hover:border-blue-400`}
      onClick={handleCardClick}
    >
      <div className="flex justify-between items-center mb-3">
        <Space>
          {getServiceIcon(serviceTicket.serviceType)}
          <Text strong>{capitalize(serviceTicket.serviceType)}</Text>
          <Text type="secondary">#{serviceTicket.serial}</Text>
        </Space>
       <div className="border border-gray-300 bg-gray-50 px-3 py-2 rounded">
         <div className="font-bold text-black">
           {formatDateTimeWithDay(serviceTicket.serviceDatetime, serviceTicket.startDatetime, serviceTicket.duration)}
         </div>
       </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="flex items-center space-x-1 mb-1">
            <MapPin size={14} />
            <Text type="secondary" className="text-xs">Location</Text>
          </div>
          <div className="text-sm font-medium">
            {serviceTicket.tempLocationData?.name || serviceTicket.client?.companyName || 'N/A'}
          </div>
          <div className="text-sm">
            {serviceTicket.location
              ? formatFullAddress(serviceTicket.location)
              : serviceTicket.tempLocationData
                ? `${serviceTicket.tempLocationData.city || ''}${serviceTicket.tempLocationData.state ? ', ' + serviceTicket.tempLocationData.state : ''}${serviceTicket.tempLocationData.zip ? ' ' + serviceTicket.tempLocationData.zip : ''}`.trim() || 'N/A'
                : 'N/A'}
          </div>
          <div className="text-xs">
            {serviceTicket.tempLocationData?.name || (serviceTicket.client ? getClientName(serviceTicket.client) : 'N/A')} {serviceTicket.client?.contactPhone && `- ${serviceTicket.client.contactPhone}`}
          </div>
        </div>

        <div>
          <div className="flex items-center space-x-1 mb-1">
            <Package size={14} />
            <Text type="secondary" className="text-xs">Container Info</Text>
          </div>
          <div className="text-sm">
            {serviceTicket.inventories?.length > 0
              ? `${serviceTicket.inventories.length} items • ${inventoryStats.totalCubicFeet} ft³`
              : serviceTicket.estimateTemplates?.length > 0
                ? <><span className="text-purple-600">Est:</span> {serviceTicket.estimateTemplates.reduce((sum: number, t: any) => sum + (t.quantity || 0), 0)} pkgs</>
                : serviceTicket.estimatedItemCount
                  ? <><span className="text-purple-600">Est:</span> ~{serviceTicket.estimatedItemCount} items</>
                  : '0 items'}
          </div>
        </div>
      </div>

      {serviceTicket.staffs && serviceTicket.staffs.length > 0 && (
        <div className="mt-3 pt-3 border-t">
          <div className="flex items-center space-x-1 mb-1">
            <Users size={14} />
            <Text type="secondary" className="text-xs">Staff & Vehicle</Text>
          </div>
          <div className="text-sm">
            {serviceTicket.vessel?.name && (
              <span className="font-medium">{serviceTicket.vessel.name} • </span>
            )}
            {serviceTicket.staffs.map((staff, index) => {
              return (
                <span key={staff.id}>
                  {formatStaffName(staff)}
                  {index < serviceTicket.staffs!.length - 1 && ", "}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Add-On Services */}
      {serviceTicket.addOnServices && serviceTicket.addOnServices.length > 0 && (
        <div className="mt-3 pt-3 border-t">
          <div className="flex items-center space-x-1 mb-1">
            <Package size={14} />
            <Text type="secondary" className="text-xs">Add-On Services</Text>
          </div>
          <div className="text-sm">
            {serviceTicket.addOnServices.map((service, index) => (
              <span key={service.id}>
                {(service as any).service?.name || service.name}
                {index < serviceTicket.addOnServices!.length - 1 && ", "}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Service Ticket Status - Lower Right Corner */}
      <div className="absolute bottom-3 right-3">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getServiceTicketStatusColor(serviceTicket.status)}`}>
          {formatServiceTicketStatus(serviceTicket.status)}
        </span>
      </div>
    </Card>
    );
  };

  const renderSimpleScheduleView = () => {
    const getServiceTicketStatusColor = (status: string) => {
      switch (status?.toLowerCase()) {
        case 'completed':
          return 'bg-blue-100 text-blue-800';
        case 'scheduled':
          return 'bg-green-100 text-green-800';
        case 'cancelled':
          return 'bg-red-100 text-red-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };

    const formatServiceTicketStatus = (status: string) => {
      return status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase() || 'N/A';
    };

    // Navigation handler
    const handleCardClick = (e: React.MouseEvent) => {
      // Don't navigate if clicking the expand/collapse button
      if ((e.target as HTMLElement).closest('.expand-toggle')) {
        return;
      }

      const targetBookingId = bookingId || serviceTicket.jobTicket?.id;

      if (!targetBookingId) {
        console.warn('No booking ID available for navigation', {
          passedBookingId: bookingId,
          serviceTicket: serviceTicket,
          jobTicket: serviceTicket.jobTicket
        });
        return;
      }
      navigate(`/bookings/${targetBookingId}?tab=services&serviceId=${serviceTicket.id}`);
    };

    // Get background style based on service type and vessel color
    const backgroundStyle = getServiceBackgroundStyle(serviceTicket.serviceType, serviceTicket.vessel?.color);

    return (
      <div
        className={`rounded-lg border border-gray-200 shadow-sm cursor-pointer hover:shadow-lg transition-shadow ${className}`}
        style={backgroundStyle}
        onClick={handleCardClick}
      >
        {/* Always-Visible Header */}
        <div
          className="p-4 cursor-pointer hover:bg-white hover:bg-opacity-60 transition-colors border-b border-gray-200 expand-toggle"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          {/* First Row - Main Info */}
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {/* Service Type Icon & Name */}
              <div className="flex items-center gap-2 min-w-fit">
                <div className="flex-shrink-0">
                  {getServiceIcon(serviceTicket.serviceType)}
                </div>
                <span className="font-semibold text-gray-900">{capitalize(serviceTicket.serviceType)}</span>
              </div>

              {/* Serial */}
              <span className="text-sm text-gray-500 font-mono">#{serviceTicket.serial}</span>

              {/* Client & Booking Client */}
              <div className="flex items-center gap-2 truncate">
                <span className="text-sm font-medium text-gray-900 truncate">
                  {serviceTicket.tempLocationData?.name || serviceTicket.client?.companyName || 'No Client'}
                </span>
                {serviceTicket.jobTicket?.client?.companyName &&
                 serviceTicket.jobTicket.client.companyName !== (serviceTicket.tempLocationData?.name || serviceTicket.client?.companyName) && (
                  <span className="text-xs text-gray-500 italic truncate">
                    ({serviceTicket.jobTicket.client.companyName})
                  </span>
                )}
              </div>

              {/* Status Badge */}
              <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${getServiceTicketStatusColor(serviceTicket.status)}`}>
                {formatServiceTicketStatus(serviceTicket.status)}
              </span>

              {/* Date & Time */}
              <span className="text-sm text-gray-600 whitespace-nowrap ml-auto">
                {dayjs(serviceTicket.serviceDatetime).format('MMM D, YYYY')} • {formatToAMPM(serviceTicket.startDatetime)}
              </span>
            </div>

            {/* Expand/Collapse Icon */}
            <div className="text-gray-400 ml-4 flex-shrink-0 text-lg font-bold">
              {isExpanded ? '−' : '+'}
            </div>
          </div>

          {/* Second Row - Location, Vehicle, Staff */}
          <div className="flex items-center gap-6 text-xs text-gray-600 ml-8">
            {/* Location */}
            <div className="flex items-center gap-1.5">
              <MapPin size={12} />
              <span className="truncate max-w-xs">
                {serviceTicket.location
                  ? formatFullAddress(serviceTicket.location)
                  : serviceTicket.tempLocationData
                    ? `${serviceTicket.tempLocationData.city || ''}${serviceTicket.tempLocationData.state ? ', ' + serviceTicket.tempLocationData.state : ''}`.trim() || 'N/A'
                    : 'N/A'}
              </span>
            </div>

            {/* Vehicle */}
            {serviceTicket.vessel?.name && (
              <div className="flex items-center gap-1.5">
                <Truck size={12} />
                <span>{serviceTicket.vessel.name}</span>
              </div>
            )}

            {/* Staff Count with Names */}
            {serviceTicket.staffs && serviceTicket.staffs.length > 0 && (
              <div className="flex items-center gap-1.5">
                <Users size={12} />
                <span>
                  {serviceTicket.staffs.length} staff
                  {(() => {
                    const firstFiveStaff = serviceTicket.staffs.slice(0, 5);
                    const staffNames = firstFiveStaff
                      .map((staff: any) => {
                        const firstName = staff.user?.firstName || '';
                        const lastName = staff.user?.lastName || '';
                        return `${firstName} ${lastName}`.trim() || 'Unknown';
                      })
                      .filter(Boolean)
                      .join(', ');

                    return staffNames ? (
                      <span className="text-gray-400 ml-1">({staffNames})</span>
                    ) : null;
                  })()}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Expandable Content */}
        {isExpanded && (
          <div className="p-4 space-y-4 bg-white bg-opacity-50">
            {/* Service Details and Client Info Side by Side */}
            <div className="grid grid-cols-2 gap-6">
              {/* Service Details */}
              <div className="space-y-2 text-sm">
                <div className="font-bold text-gray-900 mb-3 text-base">Service Details</div>
                <div className="flex">
                  <span className="text-gray-600 w-28">Date:</span>
                  <span className="font-medium text-gray-900">
                    {dayjs(serviceTicket.serviceDatetime).format('ddd, MMM D, YYYY')}
                  </span>
                </div>
                <div className="flex">
                  <span className="text-gray-600 w-28">Time:</span>
                  <span className="font-medium text-gray-900">{formatToAMPM(serviceTicket.startDatetime)}</span>
                </div>
                {serviceTicket.duration && (
                  <div className="flex">
                    <span className="text-gray-600 w-28">Duration:</span>
                    <span className="font-medium text-gray-900">{serviceTicket.duration}h</span>
                  </div>
                )}
                <div className="flex">
                  <span className="text-gray-600 w-28">Location:</span>
                  <span className="font-medium text-gray-900">
                    {serviceTicket.location
                      ? formatFullAddress(serviceTicket.location)
                      : serviceTicket.tempLocationData
                        ? `${serviceTicket.tempLocationData.city || ''}${serviceTicket.tempLocationData.state ? ', ' + serviceTicket.tempLocationData.state : ''}${serviceTicket.tempLocationData.zip ? ' ' + serviceTicket.tempLocationData.zip : ''}`.trim() || 'N/A'
                        : 'N/A'}
                  </span>
                </div>
                <div className="flex">
                  <span className="text-gray-600 w-28">Vehicle:</span>
                  <span className="font-medium text-gray-900">{serviceTicket.vessel?.name || "Not assigned"}</span>
                </div>
              </div>

              {/* Client Info */}
              <div className="space-y-2 text-sm">
                <div className="font-bold text-gray-900 mb-3 text-base">Service Client Info</div>
                <div className="flex">
                  <span className="text-gray-600 w-28">Company:</span>
                  <span className="font-medium text-gray-900">{serviceTicket.tempLocationData?.name || serviceTicket.client?.companyName || 'N/A'}</span>
                </div>
                <div className="flex">
                  <span className="text-gray-600 w-28">Contact:</span>
                  <span className="font-medium text-gray-900">{serviceTicket.tempLocationData?.name || getClientName(serviceTicket.client)}</span>
                </div>
                {serviceTicket.client?.contactPhone && (
                  <div className="flex">
                    <span className="text-gray-600 w-28">Phone:</span>
                    <span className="font-medium text-gray-900">{serviceTicket.client.contactPhone}</span>
                  </div>
                )}
                {serviceTicket.client?.contactEmail && (
                  <div className="flex">
                    <span className="text-gray-600 w-28">Email:</span>
                    <span className="font-medium text-gray-900">{serviceTicket.client.contactEmail}</span>
                  </div>
                )}
                {/* Special Instructions - try multiple possible field locations */}
                {((serviceTicket.location as any)?.specialInstructions ||
                  (serviceTicket.location as any)?.notes ||
                  (serviceTicket as any)?.locationNotes ||
                  (serviceTicket as any)?.serviceNotes) && (
                  <div className="mt-3 pt-3 border-t border-gray-300">
                    <div className="flex flex-col">
                      <span className="text-gray-600 w-28 mb-1">Special Instructions:</span>
                      <span className="font-medium text-gray-900">
                        {(serviceTicket.location as any)?.specialInstructions ||
                         (serviceTicket.location as any)?.notes ||
                         (serviceTicket as any)?.locationNotes ||
                         (serviceTicket as any)?.serviceNotes}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Scope - Full Width */}
            {serviceTicket.scope && (
              <div className="border-t border-gray-200 pt-4">
                <div className="font-bold text-gray-900 mb-2 text-base">Scope</div>
                <div className="text-sm text-gray-700 bg-white p-3 rounded border border-gray-200">{serviceTicket.scope}</div>
              </div>
            )}

            {/* Staff */}
            {serviceTicket.staffs && serviceTicket.staffs.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <div className="font-bold text-gray-900 mb-2 text-base">Staff</div>
                <div className="flex flex-wrap gap-2">
                  {serviceTicket.staffs.map((staff) => {
                    return (
                      <Tag key={staff.id} color="blue">
                        {formatStaffName(staff)}
                      </Tag>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Add-On Services */}
            {serviceTicket.addOnServices && serviceTicket.addOnServices.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <div className="font-bold text-gray-900 mb-2 text-base">Add-On Services</div>
                <div className="flex flex-wrap gap-2">
                  {serviceTicket.addOnServices.map((service) => (
                    <Tag key={service.id} color="purple">
                      {service.service?.name || service.name}
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            {/* Container Summary */}
            {(serviceTicket.inventories?.length > 0 || serviceTicket.estimateTemplates?.length > 0 || serviceTicket.estimatedItemCount) && (
              <div className="border-t border-gray-200 pt-4">
                <div className="font-bold text-gray-900 mb-2 text-base">
                  {serviceTicket.inventories?.length > 0 ? 'Container Summary' : 'Estimated Container Info'}
                </div>
                {serviceTicket.inventories?.length > 0 ? (
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-xs text-gray-500">Items</div>
                      <div className="font-medium">{serviceTicket.inventories.length} packages</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Volume</div>
                      <div className="font-medium">{inventoryStats.totalCubicFeet} ft³</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Weight</div>
                      <div className="font-medium">{inventoryStats.totalWeight || 0} lbs</div>
                    </div>
                  </div>
                ) : serviceTicket.estimateTemplates?.length > 0 ? (
                  <div className="space-y-2">
                    {serviceTicket.estimateTemplates.map((template: any, idx: number) => (
                      <div key={idx} className="flex justify-between items-center bg-purple-50 rounded p-2">
                        <span className="font-medium text-purple-800">{template.templateName}</span>
                        <span className="text-purple-600">{template.quantity} pkgs</span>
                      </div>
                    ))}
                    <div className="text-right font-bold text-purple-700 pt-2 border-t">
                      Total: {serviceTicket.estimateTemplates.reduce((sum: number, t: any) => sum + (t.quantity || 0), 0)} pkgs
                    </div>
                  </div>
                ) : (
                  <div className="text-purple-600 font-medium">
                    ~{serviceTicket.estimatedItemCount} estimated items
                  </div>
                )}
              </div>
            )}

            {/* Inventory Details */}
            {serviceTicket.inventories && serviceTicket.inventories.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <div className="font-bold text-gray-900 mb-2 text-base">Inventory Details</div>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {serviceTicket.inventories.map((item) => (
                    <div
                      key={item.id}
                      className="bg-white border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => handleViewInInventory(item)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{item.title || "Untitled"}</div>
                          {item.clientInternalRef && (
                            <div className="text-xs text-gray-500 mt-1">Client Ref: {item.clientInternalRef}</div>
                          )}
                          {(item as any).artist && (
                            <div className="text-xs text-gray-600 mt-1">Artist: {(item as any).artist}</div>
                          )}
                          <div className="text-sm text-gray-600 mt-1">
                            {formatDimensions(item)}
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-xs text-gray-500">Type</div>
                          <div className="text-xs font-medium text-gray-900">
                            {item.containerType || item.packingType || "N/A"}
                          </div>
                          <div className="text-xs font-medium text-gray-900">
                            {item.containerType || item.packingType || "N/A"}
                          </div>
                          {item.weight && (
                            <div className="text-xs text-gray-600 mt-1">{item.weight} lbs</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Supplies */}
            {serviceTicket.supplyItems && serviceTicket.supplyItems.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <div className="font-bold text-gray-900 mb-2 text-base">Supplies</div>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {serviceTicket.supplyItems.map((item) => item.supply ? (
                    <div
                      key={item.supply.id}
                      className="flex justify-between items-center text-sm p-2 bg-white rounded border border-gray-200"
                    >
                      <div>
                        <div className="font-medium">{item.supply.name || "Untitled"}</div>
                        <div className="text-gray-500 text-xs">
                          Quantity: {item.quantity}
                        </div>
                      </div>
                    </div>
                  ) : null)}
                </div>
              </div>
            )}

            {/* Service Completion Details */}
            {(serviceTicket.completedAt || serviceTicket.completed_at) && (
              <div className="border-t border-gray-200 pt-4">
                <ServiceCompletionDetails serviceTicket={serviceTicket} showTitle={true} />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

const renderDetailedView = () => {
  // Add this click handler function
  const handleCardClick = () => {
    if (!serviceTicket.jobTicket?.id) {
      console.warn('No job ticket ID available for navigation');
      return;
    }
    navigate(`/bookings/${serviceTicket.jobTicket.id}?tab=services&serviceId=${serviceTicket.id}`);
  };

  return (
    <div className={`${className}`}>
      {/* Header Section */}
      {showHeader && (
        <div className="bg-gray-50 p-4 rounded-t-lg border-b">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              {getServiceIcon(serviceTicket.serviceType)}
              <span className="text-lg font-bold text-gray-900">{capitalize(serviceTicket.serviceType)}</span>
              <span className="text-gray-500">#{serviceTicket.serial}</span>
            </div>
            <span className={`px-3 py-1.5 rounded-md text-sm font-semibold ${getServiceTicketStatusColor(serviceTicket.status)}`}>
              {formatServiceTicketStatus(serviceTicket.status)}
            </span>
          </div>

          {/* Date & Client Info */}
          <div className="flex items-center justify-between">
            {(serviceTicket.tempLocationData?.name || serviceTicket.client?.companyName) && (
              <div className="text-base font-semibold text-gray-800">
                {serviceTicket.tempLocationData?.name || serviceTicket.client?.companyName}
              </div>
            )}
            {serviceTicket.serviceDatetime && (
              <div className="text-right">
                <div className="text-sm font-medium text-gray-700">
                  {new Date(serviceTicket.serviceDatetime).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="p-4 space-y-4">
        {/* Service Details Card */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm">
          <div className="text-lg font-bold text-gray-900 mb-3">Service Details</div>
          <div className="space-y-2 text-base">
            <div>
              <span className="text-gray-600">Service Type: </span>
              <span className="font-medium text-gray-900">{capitalize(serviceTicket.serviceType)}</span>
            </div>
            <div>
              <span className="text-gray-600">Service Date: </span>
              <span className="font-medium text-gray-900">
                {new Date(serviceTicket.serviceDatetime).toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Start Time: </span>
              <span className="font-medium text-gray-900">{formatToAMPM(serviceTicket.startDatetime)}</span>
            </div>
            {serviceTicket.duration && (
              <div>
                <span className="text-gray-600">Est. Duration: </span>
                <span className="font-medium text-gray-900">{serviceTicket.duration} hour{serviceTicket.duration !== 1 ? 's' : ''}</span>
              </div>
            )}
            {serviceTicket.jobTicket?.client?.companyName && (
              <div>
                <span className="text-gray-600">Booking Client: </span>
                <span className="font-medium text-gray-900">{serviceTicket.jobTicket.client.companyName}</span>
              </div>
            )}
          </div>
        </div>

        {/* Service Client Info Card */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm">
          <div className="text-lg font-bold text-gray-900 mb-3">Service Client Info</div>
          <div className="space-y-2 text-base">
            <div>
              <span className="text-gray-600">Company: </span>
              <span className="font-medium text-gray-900">{serviceTicket.tempLocationData?.name || serviceTicket.client?.companyName || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-600">Contact: </span>
              <span className="font-medium text-gray-900">{serviceTicket.tempLocationData?.name || getClientName(serviceTicket.client)}</span>
            </div>
            {serviceTicket.client?.contactPhone && (
              <div>
                <span className="text-gray-600">Phone: </span>
                <span className="font-medium text-gray-900">{serviceTicket.client.contactPhone}</span>
              </div>
            )}
            <div>
              <span className="text-gray-600">Address: </span>
              <span className="font-medium text-gray-900">
                {serviceTicket.location
                  ? formatFullAddress(serviceTicket.location)
                  : serviceTicket.tempLocationData
                    ? `${serviceTicket.tempLocationData.city || ''}${serviceTicket.tempLocationData.state ? ', ' + serviceTicket.tempLocationData.state : ''}${serviceTicket.tempLocationData.zip ? ' ' + serviceTicket.tempLocationData.zip : ''}`.trim() || 'N/A'
                    : 'N/A'}
              </span>
            </div>
            {(serviceTicket.location?.specialInstructions) && (
              <div className="pt-2 mt-2 border-t border-gray-300">
                <span className="text-gray-600">Special Instructions: </span>
                <div className="text-gray-900 mt-1">{serviceTicket.location.specialInstructions}</div>
              </div>
            )}
          </div>
        </div>

        {/* Scope Card */}
        {serviceTicket.scope && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <FileText size={18} />
              <span className="text-lg font-bold">Scope</span>
            </div>
            <div className="text-gray-900">{serviceTicket.scope}</div>
          </div>
        )}

        {/* Add-On Services Card */}
        {serviceTicket.addOnServices && serviceTicket.addOnServices.length > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <FileText size={18} />
              <span className="text-lg font-bold">Add-On Services</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {serviceTicket.addOnServices.map((service: any) => (
                <Tag key={service.id} color="purple">
                  {service.service?.name || service.name}
                </Tag>
              ))}
            </div>
          </div>
        )}

        {/* Vehicle and Staff Side by Side */}
        <div className="grid grid-cols-2 gap-4">
          {/* Vehicle Card */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <Truck size={18} />
              <span className="text-lg font-bold">Vehicle</span>
            </div>
            <div className="text-lg font-medium text-gray-900">{serviceTicket.vessel?.name || "Not assigned"}</div>
          </div>

          {/* Staff Card */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <Users size={18} />
              <span className="text-lg font-bold">Staff Assigned</span>
            </div>
            <div className="text-base text-gray-900">
              {serviceTicket.staffs && serviceTicket.staffs.length > 0
                ? serviceTicket.staffs.map((staff, index) => {
                    return (
                      <span key={staff.id} className="inline-block mr-3 mb-1">
                        {formatStaffName(staff)}
                        {staff.isLead && " (L)"}
                        {staff.isDriver && " (D)"}
                        {index < serviceTicket.staffs.length - 1 && ","}
                      </span>
                    );
                  })
                : "No staff assigned"}
            </div>
          </div>
        </div>

        {/* EXPO Schedule Card - For ON_SITE_SERVICE / EXPO services */}
        {(() => {
          const isExpoService = serviceTicket.serviceType === ServiceType.ON_SITE_SERVICE ||
            (serviceTicket.serviceType as string) === 'SITE_SERVICE' ||
            (serviceTicket.serviceType as string) === 'ON_SITE_SERVICE' ||
            serviceTicket.isExpoDay;

          // Get positions from multiple sources
          const positions = serviceTicket.positions ||
            ((serviceTicket as any).expoDays?.[0]?.positions) ||
            [];

          // Get day timing info (check both camelCase and snake_case)
          const dayStartTime = (serviceTicket as any).dayStartTime || (serviceTicket as any).day_start_time;
          const dayDuration = (serviceTicket as any).dayDuration || (serviceTicket as any).day_duration;

          if (!isExpoService) return null;

          return (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 shadow-sm">
              <div className="flex items-center gap-2 text-green-700 mb-3">
                <Briefcase size={18} />
                <span className="text-lg font-bold">
                  EXPO Schedule
                  {serviceTicket.dayLabel && (
                    <span className="ml-2 text-sm font-normal text-green-600">
                      ({serviceTicket.dayLabel})
                    </span>
                  )}
                </span>
              </div>

              {/* Day Timing Info */}
              {(dayStartTime || dayDuration) && (
                <div className="bg-white border border-green-100 rounded-lg p-3 mb-3">
                  <div className="grid grid-cols-2 gap-4">
                    {dayStartTime && (
                      <div>
                        <div className="text-xs text-green-600 font-medium uppercase">Event Start</div>
                        <div className="text-base font-bold text-gray-900">{dayStartTime}</div>
                      </div>
                    )}
                    {dayDuration && (
                      <div>
                        <div className="text-xs text-green-600 font-medium uppercase">Event Duration</div>
                        <div className="text-base font-bold text-gray-900">{dayDuration}h</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Positions List */}
              {positions.length > 0 && (
                <div className="space-y-2">
                  {positions.map((pos: any, index: number) => (
                    <div key={pos.id || index} className="bg-white border border-green-100 rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-semibold text-gray-900">
                            {pos.position?.name || pos.positionName || 'Position'}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            <span className="inline-flex items-center gap-1">
                              <Clock size={12} />
                              Start: {pos.startTime || 'TBD'}
                            </span>
                            <span className="mx-2">•</span>
                            <span>Duration: {pos.duration}h</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-500">Qty: {pos.quantity}</div>
                          <div className="text-sm font-medium text-green-700">
                            ${Number(pos.hourlyRate || 0)}/hr
                          </div>
                          {pos.subtotal && (
                            <div className="text-xs text-gray-500 mt-1">
                              Subtotal: ${Number(pos.subtotal).toFixed(2)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {/* Total */}
                  <div className="flex justify-end pt-2 border-t border-green-200">
                    <div className="text-right">
                      <span className="text-gray-600 mr-2">Total:</span>
                      <span className="font-bold text-green-700">
                        ${positions.reduce((sum: number, pos: any) => sum + Number(pos.subtotal || (Number(pos.quantity) * Number(pos.duration) * Number(pos.hourlyRate))), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Message if no data yet */}
              {positions.length === 0 && !dayStartTime && !dayDuration && (
                <div className="text-sm text-gray-500 italic">
                  No positions or schedule configured yet. Configure in booking details.
                </div>
              )}
            </div>
          );
        })()}

        {/* Time In/Out Card - For services with timeIn or timeOut */}
        {(serviceTicket.timeIn || serviceTicket.timeOut) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 shadow-sm">
            <div className="flex items-center gap-2 text-blue-700 mb-3">
              <Clock size={18} />
              <span className="text-lg font-bold">Time Tracking</span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {/* Time In */}
              <div className="text-center p-2 bg-white rounded border border-blue-100">
                <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
                  <LogIn size={14} />
                  <span className="text-xs font-medium">Time In</span>
                </div>
                <div className="text-base font-bold text-gray-900">
                  {serviceTicket.timeIn
                    ? dayjs(serviceTicket.timeIn).format('h:mm A')
                    : '—'}
                </div>
                {serviceTicket.timeIn && (
                  <div className="text-xs text-gray-500 mt-1">
                    {dayjs(serviceTicket.timeIn).format('MMM D')}
                  </div>
                )}
              </div>

              {/* Time Out */}
              <div className="text-center p-2 bg-white rounded border border-blue-100">
                <div className="flex items-center justify-center gap-1 text-red-600 mb-1">
                  <LogOut size={14} />
                  <span className="text-xs font-medium">Time Out</span>
                </div>
                <div className="text-base font-bold text-gray-900">
                  {serviceTicket.timeOut
                    ? dayjs(serviceTicket.timeOut).format('h:mm A')
                    : '—'}
                </div>
                {serviceTicket.timeOut && (
                  <div className="text-xs text-gray-500 mt-1">
                    {dayjs(serviceTicket.timeOut).format('MMM D')}
                  </div>
                )}
              </div>

              {/* Actual Duration */}
              <div className="text-center p-2 bg-white rounded border border-blue-100">
                <div className="flex items-center justify-center gap-1 text-blue-600 mb-1">
                  <Clock size={14} />
                  <span className="text-xs font-medium">Actual Duration</span>
                </div>
                <div className="text-base font-bold text-gray-900">
                  {serviceTicket.timeIn && serviceTicket.timeOut
                    ? `${((new Date(serviceTicket.timeOut).getTime() - new Date(serviceTicket.timeIn).getTime()) / (1000 * 60 * 60)).toFixed(1)}h`
                    : serviceTicket.duration
                      ? `${serviceTicket.duration}h (est.)`
                      : '—'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Supplies Card */}
        {serviceTicket.supplyItems && serviceTicket.supplyItems.length > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <Package size={18} />
              <span className="text-lg font-bold">Supplies</span>
            </div>
            <div className="text-base text-gray-900">
              {serviceTicket.supplyItems.map((item: any) => item.supply ? (
                <div key={item.supply.id} className="mb-1">
                  {item.supply.name} {item.quantity && `(Qty: ${item.quantity})`}
                </div>
              ) : null)}
            </div>
          </div>
        )}

        {/* Staff Notes Card */}
        {serviceTicket.staffNotes && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm col-span-2">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <MessageSquare size={18} />
              <span className="text-lg font-bold">Staff Notes</span>
            </div>
            <div className="text-base text-gray-900">{serviceTicket.staffNotes}</div>
          </div>
        )}

        {/* Container Summary Card */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm col-span-2">
          <div className="flex items-center gap-2 text-gray-600 mb-3">
            <Package size={18} />
            <span className="text-lg font-bold">Container Summary</span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-2 bg-gray-50 rounded">
              <div className="text-xs text-gray-500 mb-1">Items</div>
              <div className="text-xl font-bold text-gray-900">{serviceTicket.inventories?.length || 0}</div>
              <div className="text-xs text-gray-500">packages</div>
            </div>
            <div className="text-center p-2 bg-gray-50 rounded">
              <div className="text-xs text-gray-500 mb-1">Volume</div>
              <div className="text-xl font-bold text-gray-900">{inventoryStats.totalCubicFeet}</div>
              <div className="text-xs text-gray-500">ft³</div>
            </div>
            <div className="text-center p-2 bg-gray-50 rounded">
              <div className="text-xs text-gray-500 mb-1">Weight</div>
              <div className="text-xl font-bold text-gray-900">{inventoryStats.totalWeight || 0}</div>
              <div className="text-xs text-gray-500">lbs</div>
            </div>
          </div>
        </div>
      </div>

      {/* Inventory Details */}
      {serviceTicket.inventories && serviceTicket.inventories.length > 0 && (
        <div className="p-4 border-t">
          <div className="flex items-center gap-2 text-gray-700 mb-3">
            <Package size={18} />
            <span className="text-lg font-bold">Inventory Details</span>
            <span className="text-sm text-gray-500">({serviceTicket.inventories.length} items)</span>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {serviceTicket.inventories.map((item) => (
              <div
                key={item.id}
                className="bg-gray-50 border rounded-lg p-3 hover:bg-blue-50 hover:border-blue-300 transition-colors cursor-pointer group"
                onClick={() => {
                  if (onInventoryClick) {
                    onInventoryClick(item);
                  } else {
                    handleViewInInventory(item);
                  }
                }}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900">{item.title || "Untitled"}</div>
                      <ChevronRight size={14} className="text-gray-400 group-hover:text-blue-500 transition-colors" />
                    </div>
                    {item.clientInternalRef && (
                      <div className="text-xs text-gray-500 mt-1">Client Ref: {item.clientInternalRef}</div>
                    )}
                    {item.artist && (
                      <div className="text-xs text-gray-600 mt-1">Artist: {item.artist}</div>
                    )}
                    <div className="text-sm text-gray-600 mt-1">
                      {formatDimensions(item)}
                    </div>
                    {item.weight && (
                      <div className="text-xs text-gray-600 mt-1">Weight: {item.weight} lbs</div>
                    )}
                  </div>
                  <div className="text-right ml-4">
                    <div className="text-xs text-gray-500">Type</div>
                    <div className="text-sm font-medium text-gray-900">
                      {item.containerType || item.packingType || "N/A"}
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {item.containerType || item.packingType || "N/A"}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

  switch (variant) {
    case "compact":
      return renderCompactView();
    case "summary":
      return renderSummaryView();
    case "simpleschedule":
      return renderSimpleScheduleView();
    case "detailed":
    default:
      return renderDetailedView();
  }
};
